/**
 * Pet portrait + HP readout for the player frame (Slice 2). Renders 3 slots:
 * slots within petCap show active pet or empty unlocked; slots beyond petCap
 * show a locked-icon placeholder with a hover hint listing cap-extending items.
 * DOM built once in createPetFrame(); refresh() only toggles state each frame.
 *
 * Slice 3 Task 7 addition: hovering an active pet slot shows a stat tooltip
 * with level, damage, HP/HPmax, and active mastery behaviors derived from the
 * owner's synced taughtPets bitmask.
 *
 * Spec: docs/design/17-pets.md §"Pet command UI"; design §Slice 2 + §Slice 3.
 */

import { PET_UPGRADES } from "@tribez/shared";
import { creatureDisplayName } from "../display-names";

const CAP_HINT =
  "Locked. +1 pet cap from: Obedience Rod, Master Ranger's Maille, " +
  "Bear Shaman's Shroud, Beastmaster's Vambrace, Crow Keeper's Bracers, " +
  "Hunting Machete, or Vigilante's Crest.";

export interface PetData {
  creatureType: string;
  hp: number;
  hpMax: number;
  level: number;
  dmg: number;
}

export interface PetFrame {
  refresh(args: {
    pets: PetData[];
    petCap: number;
    masteryMask: number;
  }): void;
  destroy(): void;
}

interface SlotRefs {
  el: HTMLDivElement;
  name: HTMLDivElement;
  fill: HTMLDivElement;
  text: HTMLDivElement;
  lock: HTMLDivElement;
  body: HTMLDivElement;
  tooltip: HTMLDivElement;
}

const SLOT_BASE_CSS =
  "width:54px;height:64px;border-radius:8px;background:rgba(20,24,32,.8);" +
  "border:1px solid #2a3140;display:flex;flex-direction:column;align-items:center;" +
  "justify-content:flex-end;padding:3px;color:#e8e8e8;font:11px/1.1 sans-serif;" +
  "position:relative;box-sizing:border-box;";

const TOOLTIP_CSS =
  "position:fixed;z-index:200;pointer-events:none;display:none;" +
  "background:rgba(14,18,28,.95);border:1px solid #3a4558;border-radius:7px;" +
  "padding:7px 10px;color:#e8e8e8;font:12px/1.5 sans-serif;min-width:170px;max-width:230px;" +
  "box-shadow:0 3px 12px rgba(0,0,0,.7);";

function makeSlot(): SlotRefs {
  const el = document.createElement("div");
  el.className = "pet-slot";
  el.style.cssText = SLOT_BASE_CSS;

  // lock glyph (shown when slot is locked)
  const lock = document.createElement("div");
  lock.className = "pet-slot-lock";
  lock.textContent = "🔒"; // 🔒
  lock.style.cssText =
    "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);" +
    "font-size:20px;display:none;";
  el.appendChild(lock);

  // body: name + hp bar + hp text (shown when slot has a pet)
  const body = document.createElement("div");
  body.className = "pet-slot-body";
  body.style.cssText =
    "display:none;flex-direction:column;align-items:center;width:100%;";
  el.appendChild(body);

  const name = document.createElement("div");
  name.className = "pet-name";
  // Names re-themed long ("Sabertooth", "Columbian Mammoth") vs the old raw
  // "Wolf"/"Bear" — clamp font + ellipsis so a long name can't blow out the
  // 54px slot. Full name is always legible in the hover tooltip header.
  name.style.cssText =
    "font-weight:600;text-align:center;font-size:10px;line-height:1.1;" +
    "width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";
  body.appendChild(name);

  const bar = document.createElement("div");
  bar.className = "pet-hp-bar";
  bar.style.cssText =
    "width:100%;height:6px;border-radius:3px;background:#3a1414;margin-top:3px;overflow:hidden;";
  body.appendChild(bar);

  const fill = document.createElement("div");
  fill.className = "pet-hp-fill";
  fill.style.cssText = "height:100%;width:100%;background:#5ad15a;";
  bar.appendChild(fill);

  const text = document.createElement("div");
  text.className = "pet-hp-text";
  text.style.cssText = "margin-top:1px;";
  body.appendChild(text);

  // Tooltip div — appended to body so it inherits stacking context above game canvas.
  // Positioned in screen-space via mousemove so it follows the cursor naturally.
  const tooltip = document.createElement("div");
  tooltip.className = "pet-stat-tooltip";
  tooltip.style.cssText = TOOLTIP_CSS;
  document.body.appendChild(tooltip);

  return { el, name, fill, text, lock, body, tooltip };
}

/**
 * Build the behaviors list for a pet tooltip.
 * Mirrors the activation gates in recomputePetStats (server) — display-only.
 */
function buildBehaviorLines(creatureType: string, level: number, masteryMask: number): string[] {
  const lines: string[] = [];
  const hasBit = (b: number) => (masteryMask & (1 << b)) !== 0;

  // bit0 ThickHide — always active when owned
  if (hasBit(PET_UPGRADES[0]!.bit)) {
    lines.push("Thick Hide (+2 armor, +1 regen)");
  }

  // bit1 InspirationalMaster — passive XP only ticks below L3 (pets/abilities.ts);
  // the dmg/level bonus is already baked into the synced dmg value.
  if (hasBit(PET_UPGRADES[1]!.bit) && level < 3) {
    lines.push("Inspirational (passive XP/sec)");
  }

  // bit2 NaturalTenacity — always active when owned
  if (hasBit(PET_UPGRADES[2]!.bit)) {
    lines.push("Natural Tenacity (−30% vs wild/ghoul)");
  }

  // bit3 Companionship — requires level >= 2
  if (hasBit(PET_UPGRADES[3]!.bit) && level >= 2) {
    lines.push("Companionship (+4 dmg, +15% atk spd)");
  }

  // bit4 PredatoryKnowledge — requires level >= 2; per-species bonus
  if (hasBit(PET_UPGRADES[4]!.bit) && level >= 2) {
    let speciesLine = "";
    if (creatureType === "Wolf" || creatureType === "Lyote") {
      speciesLine = " · first-attack +25%";
    } else if (creatureType === "Bear") {
      speciesLine = " · +2 regen + ally aura";
    } else if (creatureType === "Nightwisp") {
      speciesLine = " · every-3rd +5 magic";
    }
    lines.push(`Predatory (+10% HP${speciesLine})`);
  }

  // bit5 Viciousness — requires level >= 2
  if (hasBit(PET_UPGRADES[5]!.bit) && level >= 2) {
    lines.push("Viciousness (+10% move, ignore 25% armor)");
  }

  // bit6 Loyalty — requires level >= 3
  if (hasBit(PET_UPGRADES[6]!.bit) && level >= 3) {
    lines.push("Loyalty (revives at lodge)");
  }

  return lines;
}

// ---------------------------------------------------------------------------
// Safe DOM helpers — no innerHTML; all text written via textContent.
// ---------------------------------------------------------------------------

function makeRow(label: string, value: string, valueColor?: string): HTMLDivElement {
  const row = document.createElement("div");
  row.style.cssText = "display:flex;justify-content:space-between;gap:12px;";
  const lspan = document.createElement("span");
  lspan.style.color = "#aaa";
  lspan.textContent = label;
  const vspan = document.createElement("span");
  if (valueColor) vspan.style.color = valueColor;
  vspan.textContent = value;
  row.appendChild(lspan);
  row.appendChild(vspan);
  return row;
}

/**
 * Rebuild the tooltip's CONTENT (DOM children) for a pet. Expensive-ish
 * (O(behaviors) node creation), so callers only invoke this on hover-enter or
 * when the hovered pet's data changes — never per mousemove.
 */
function renderTooltip(tooltip: HTMLDivElement, pet: PetData, masteryMask: number): void {
  const behaviors = buildBehaviorLines(pet.creatureType, pet.level, masteryMask);

  // Clear and rebuild via DOM (no innerHTML / XSS risk).
  tooltip.textContent = "";

  const header = document.createElement("div");
  header.style.cssText = "font-weight:700;font-size:13px;margin-bottom:4px;color:#b8d4ff;";
  header.textContent = creatureDisplayName(pet.creatureType);
  tooltip.appendChild(header);

  tooltip.appendChild(makeRow("Level", String(pet.level)));
  tooltip.appendChild(makeRow("Dmg", String(pet.dmg)));

  const hpPct = pet.hpMax > 0 ? pet.hp / pet.hpMax : 0;
  const hpColor = hpPct < 0.25 ? "#e05555" : "#5ad15a";
  tooltip.appendChild(makeRow("HP", `${pet.hp} / ${pet.hpMax}`, hpColor));

  if (behaviors.length > 0) {
    const bhdr = document.createElement("div");
    bhdr.style.cssText = "margin-top:5px;border-top:1px solid #3a4558;padding-top:4px;" +
      "color:#aaa;font-size:11px;margin-bottom:2px;";
    bhdr.textContent = "Behaviors";
    tooltip.appendChild(bhdr);
    for (const b of behaviors) {
      const brow = document.createElement("div");
      brow.style.cssText = "font-size:11px;color:#cce0ff;padding-left:4px;";
      brow.textContent = `• ${b}`;
      tooltip.appendChild(brow);
    }
  } else {
    const none = document.createElement("div");
    none.style.cssText = "margin-top:4px;font-size:11px;color:#666;";
    none.textContent = "No mastery upgrades";
    tooltip.appendChild(none);
  }
}

/**
 * Position the tooltip near the cursor — cheap (style writes only, no DOM
 * mutation). Safe to call every mousemove. Assumes content already rendered.
 */
function positionTooltip(tooltip: HTMLDivElement, mouseX: number, mouseY: number): void {
  // Position: prefer right/below cursor; flip if it would clip viewport edge.
  const GAP = 12;
  const PAD = 8;
  const tw = tooltip.offsetWidth || 200;
  const th = tooltip.offsetHeight || 100;
  let tx = mouseX + GAP;
  let ty = mouseY + GAP;
  if (tx + tw > window.innerWidth - PAD) tx = mouseX - GAP - tw;
  if (ty + th > window.innerHeight - PAD) ty = mouseY - GAP - th;
  tx = Math.max(PAD, tx);
  ty = Math.max(PAD, ty);
  tooltip.style.left = `${Math.round(tx)}px`;
  tooltip.style.top = `${Math.round(ty)}px`;
}

export function createPetFrame(): PetFrame {
  const root = document.createElement("div");
  root.id = "pet-frame";
  root.style.cssText =
    "position:fixed;left:12px;bottom:180px;display:flex;gap:6px;z-index:40;pointer-events:none;";
  document.body.appendChild(root);

  // Build a <style> block for the locked-dim modifier
  const style = document.createElement("style");
  style.textContent =
    ".pet-slot--locked { opacity:0.5; cursor:default; pointer-events:auto; }";
  document.head.appendChild(style);

  const slots: SlotRefs[] = [];
  for (let i = 0; i < 3; i++) {
    const s = makeSlot();
    root.appendChild(s.el);
    slots.push(s);
  }

  // Per-slot hover state — current pet data + masteryMask cached for the handlers.
  // `hovered` flags which slot the cursor is over, so refresh() can re-render its
  // (live HP / level / dmg) content while the tooltip is visible.
  const slotState: Array<{ pet: PetData | null; mask: number; hovered: boolean }> = [
    { pet: null, mask: 0, hovered: false },
    { pet: null, mask: 0, hovered: false },
    { pet: null, mask: 0, hovered: false },
  ];

  for (let i = 0; i < 3; i++) {
    const s = slots[i]!;
    const st = slotState[i]!;

    s.el.addEventListener("mouseenter", (e) => {
      st.hovered = true;
      if (!st.pet) return;
      // Rebuild content once on enter, then show + position.
      renderTooltip(s.tooltip, st.pet, st.mask);
      s.tooltip.style.display = "block";
      positionTooltip(s.tooltip, e.clientX, e.clientY);
    });
    s.el.addEventListener("mousemove", (e) => {
      // Cheap path: only reposition. No DOM content rebuild on cursor movement.
      if (!st.pet || s.tooltip.style.display === "none") return;
      positionTooltip(s.tooltip, e.clientX, e.clientY);
    });
    s.el.addEventListener("mouseleave", () => {
      st.hovered = false;
      s.tooltip.style.display = "none";
    });
  }

  return {
    refresh({ pets, petCap, masteryMask }) {
      for (let i = 0; i < 3; i++) {
        const slot = slots[i]!;
        const p = pets[i] ?? null;

        // Cache per-slot state for tooltip mousemove.
        slotState[i]!.pet = p;
        slotState[i]!.mask = masteryMask;

        if (p) {
          // Active pet — enable hover so tooltip can fire.
          slot.el.style.display = "flex";
          slot.el.style.pointerEvents = "auto";
          slot.el.classList.remove("pet-slot--locked");
          slot.el.title = "";
          slot.lock.style.display = "none";
          slot.body.style.display = "flex";
          slot.name.textContent = creatureDisplayName(p.creatureType);
          const pct =
            p.hpMax > 0
              ? Math.max(0, Math.min(100, (p.hp / p.hpMax) * 100))
              : 0;
          slot.fill.style.width = `${pct}%`;
          slot.text.textContent = `${p.hp} / ${p.hpMax}`;
          // If this slot is currently hovered with a visible tooltip, refresh its
          // content so live HP / level / dmg track the schema (position untouched).
          if (slotState[i]!.hovered && slot.tooltip.style.display !== "none") {
            renderTooltip(slot.tooltip, p, masteryMask);
          }
        } else if (i < petCap) {
          // Unlocked but empty — no tooltip.
          slot.el.style.display = "flex";
          slot.el.style.pointerEvents = "none";
          slot.el.classList.remove("pet-slot--locked");
          slot.el.title = "";
          slot.lock.style.display = "none";
          slot.body.style.display = "none";
          slot.tooltip.style.display = "none";
        } else {
          // Locked placeholder — tooltip not needed, title hint is fine.
          slot.el.style.display = "flex";
          slot.el.style.pointerEvents = "auto";
          slot.el.classList.add("pet-slot--locked");
          slot.el.title = CAP_HINT;
          slot.lock.style.display = "block";
          slot.body.style.display = "none";
          slot.tooltip.style.display = "none";
        }
      }
    },
    destroy() {
      root.remove();
      style.remove();
      for (const s of slots) s.tooltip.remove();
    },
  };
}
